import { FC } from 'react';
import styled from 'styled-components';
import { useAssignmentGetRideAlongCandidates } from 'api/assignment/assignment';
import { RideAlongCandidateDto } from 'api/model';
import Input from 'components/inputs/Input';
import SearchableSelect, {
  fakeSelectClassName,
  SearchableSelectItem,
  SelectOption,
} from 'components/inputs/SearchableSelect';
import { BasicFormState } from 'hooks/useForm';
import { formatTime, toInputDateTimeString } from 'utils/date-helpers';
import Routes from 'constants/Routes';
import { AssignmentField } from '../DetailedCaseFormProvider/types';
import { LabelTextButton } from './style';

/** Värdet i väljaren när bilen passageraren åker med inte körs som ett uppdrag i systemet. */
export const NO_CARRIER_IN_SYSTEM = 'none';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Hint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const PickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PickerWidth = styled.div`
  .${fakeSelectClassName} {
    width: 395px;
  }
`;

const OptionRoute = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

type CarrierOption = SelectOption<string, RideAlongCandidateDto | null>;

const candidateLabel = (c: RideAlongCandidateDto) => {
  const start = c.startTime ?? c.estimatedStartTime;
  const time = start ? formatTime(new Date(start)) : '--:--';
  return `${time} ${c.registrationNumber} – ${c.assigneeName} (${c.fromCity} → ${c.toCity})`;
};

const openCarrierCase = (carrier: RideAlongCandidateDto) =>
  window.open(
    `${Routes.search.index}/${carrier.caseID}`,
    carrier.caseID.toString()
  );

const candidateRoute = (c: RideAlongCandidateDto) =>
  `${c.fromName}, ${c.fromCity} → ${c.toName}, ${c.toCity}`;

const renderCarrierOption = (
  key: string,
  option: SelectOption<string>,
  onClick: () => void,
  isSelected: boolean
) => {
  const carrier = (option as CarrierOption).extra;
  return (
    <SearchableSelectItem key={key} onClick={onClick} selected={isSelected}>
      <div>{option.label}</div>
      {carrier && <OptionRoute>{candidateRoute(carrier)}</OptionRoute>}
    </SearchableSelectItem>
  );
};

/**
 * Sökningen i "Samåker med": varje ord i söksträngen ska finnas i regnr, förare eller från-/till-
 * fältens namn, adress och ort — så att "strängnäs linköping" hittar bilen på den sträckan.
 */
export const matchesRideAlongSearch = (
  c: RideAlongCandidateDto,
  searchString: string
) => {
  const haystack = [
    c.registrationNumber,
    c.assigneeName,
    c.fromName,
    c.fromAddress,
    c.fromCity,
    c.toName,
    c.toAddress,
    c.toCity,
  ]
    .join(' ')
    .toLowerCase();
  return searchString
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
};

/**
 * Hur länge passageraren sitter i bilen: bärarens fasta tid, annars estimat, plus elbilens
 * laddbuffert — laddningen är belagd tid som passageraren sitter med under. (En elbil har aldrig
 * fast tid, så bufferten läggs i praktiken på estimatet.)
 */
export const carrierRideDurationMs = (carrier: RideAlongCandidateDto) => {
  const base = carrier.fixedAssignmentTime || carrier.estimatedDuration || 0;
  return base > 0 ? base + (carrier.electricCarBufferTime ?? 0) : 0;
};

/**
 * Samåkningens tider och adresser hämtas från bäraren när TL väljer den: samma sträcka, samma
 * start, bärarens tid (inklusive elbilens laddbuffert) som estimerad tid, och ingen rast — benet
 * räknas själv som rast i rastkontrollen. Fylls i en gång vid valet och synkas inte därefter, så
 * TL kan justera fritt.
 *
 * Samåkningar får fast tid bara för hand av Efterkontroll eller SuperAdmin, så för alla andra
 * rensas en liggande fast tid. De två rollernas eget värde (`keepFixedTime`) lämnas orört — valet
 * av bärare ger aldrig fast tid, men tar inte heller bort en de skrivit in. Backend gör samma
 * bedömning vid spar (CaseTypeEnum.FixedTimeOnlyByHand).
 */
export const fillFromCarrier = (
  carrier: RideAlongCandidateDto,
  assignmentId: number,
  onChange: (
    assignmentId: number,
    field: AssignmentField,
    value: string
  ) => void,
  { keepFixedTime = false }: { keepFixedTime?: boolean } = {}
) => {
  const set = (field: AssignmentField, value: string) =>
    onChange(assignmentId, field, value);

  set(AssignmentField.fromName, carrier.fromName);
  set(AssignmentField.fromAddress, carrier.fromAddress);
  set(AssignmentField.fromCity, carrier.fromCity);
  set(AssignmentField.fromZip, carrier.fromZip);
  set(AssignmentField.fromCounty, String(carrier.fromCountyID ?? ''));
  set(AssignmentField.toName, carrier.toName);
  set(AssignmentField.toAddress, carrier.toAddress);
  set(AssignmentField.toCity, carrier.toCity);
  set(AssignmentField.toZip, carrier.toZip);
  set(AssignmentField.toCounty, String(carrier.toCountyID ?? ''));

  const start = carrier.estimatedStartTime ?? carrier.startTime;
  if (start) {
    set(
      AssignmentField.estimatedStartTime,
      toInputDateTimeString(new Date(start))
    );
  }

  if (!keepFixedTime) {
    set(AssignmentField.fixedAssignmentTimeInHours, '');
  }

  const durationMs = carrierRideDurationMs(carrier);
  if (durationMs) {
    set(AssignmentField.estimatedDuration, String(durationMs));
  }

  set(AssignmentField.breakTime, '0');
};

interface Props {
  assignmentId: number;
  state: BasicFormState<AssignmentField>;
  requiresCarrier: boolean;
  disabled?: boolean;
  /** Efterkontroll och SuperAdmin: valet av bärare rör inte deras fasta tid. */
  canSetFixedTimeByHand?: boolean;
  onChange(assignmentId: number, field: AssignmentField, value: string): void;
}

const RideAlongCarrierPicker: FC<Props> = ({
  assignmentId,
  state,
  requiresCarrier,
  disabled,
  canSetFixedTimeByHand = false,
  onChange,
}) => {
  const bookedTo = state[AssignmentField.bookedTo];
  const selected = state[AssignmentField.ridesAlongWithAssignmentID];
  const passengerUserId =
    Number(state[AssignmentField.assignedTo]) || undefined;
  const selectedId = Number(selected) || undefined;

  const bookedToDate = bookedTo ? new Date(bookedTo) : undefined;
  const hasValidDate = !!bookedToDate && !Number.isNaN(bookedToDate.getTime());

  const candidates = useAssignmentGetRideAlongCandidates(
    {
      date: hasValidDate ? bookedToDate : undefined,
      passengerUserId,
      includeAssignmentId: selectedId,
    },
    { query: { enabled: hasValidDate } }
  );

  if (!hasValidDate) {
    return <Hint>Ange Bokad till för att se dagens uppdrag</Hint>;
  }

  const candidateOptions: CarrierOption[] = (candidates.data ?? []).map(
    (c) => ({
      label: candidateLabel(c),
      value: String(c.assignmentID),
      extra: c,
    })
  );
  const options: CarrierOption[] = [
    {
      label: candidates.isLoading ? 'Hämtar dagens uppdrag…' : 'Välj uppdrag',
      value: '',
      extra: null,
    },
    ...candidateOptions,
    ...(requiresCarrier
      ? [
          {
            label: 'Ingen bärare i systemet (motivera)',
            value: NO_CARRIER_IN_SYSTEM,
            extra: null,
          },
        ]
      : []),
  ];

  const selectedCarrier = candidateOptions.find(
    (opt) => opt.extra?.assignmentID === selectedId
  )?.extra;

  return (
    <Wrapper>
      <PickerRow>
        <PickerWidth>
          <SearchableSelect<string, RideAlongCandidateDto | null>
            disabled={disabled}
            loading={candidates.isLoading}
            onOptionClicked={(value, _isSelected, option) => {
              onChange(
                assignmentId,
                AssignmentField.ridesAlongWithAssignmentID,
                value
              );
              if (option.extra) {
                fillFromCarrier(option.extra, assignmentId, onChange, {
                  keepFixedTime: canSetFixedTimeByHand,
                });
              }
            }}
            optionRender={renderCarrierOption}
            options={options}
            searchFilter={(searchString, opts) =>
              opts.filter(
                (opt) =>
                  !opt.extra || matchesRideAlongSearch(opt.extra, searchString)
              )
            }
            selectedValue={selected}
          />
        </PickerWidth>
        {selectedCarrier && (
          <LabelTextButton
            onClick={() => openCarrierCase(selectedCarrier)}
            title="Öppna bärarens ärende i en ny flik"
            type="button"
          >
            Öppna ärende ↗
          </LabelTextButton>
        )}
      </PickerRow>
      {selected === NO_CARRIER_IN_SYSTEM && (
        <Input
          disabled={disabled}
          onChange={(eve) =>
            onChange(
              assignmentId,
              AssignmentField.ridesAlongExemptionReason,
              eve.currentTarget.value
            )
          }
          placeholder="Varför finns bilen inte som uppdrag? (syns för Efterkontroll)"
          small
          width={395}
          value={state[AssignmentField.ridesAlongExemptionReason]}
        />
      )}
      {selectedId && (
        <Hint>
          Från, till och tider följer bäraren. Samåkningen får ingen fast tid
          från bäraren – bärarens tid
          {selectedCarrier?.isElectric ? ' inklusive laddning' : ''} blir
          estimerad tid. Rasten sätts till 0 – samåkningen räknas själv som
          rast.
        </Hint>
      )}
    </Wrapper>
  );
};

/** Läsläget: vilken bil samåkningen åker med, med länk till bärarens ärende. */
export const RideAlongCarrierSummary: FC<{
  carrierAssignmentId: number;
  bookedTo: Date;
}> = ({ carrierAssignmentId, bookedTo }) => {
  const candidates = useAssignmentGetRideAlongCandidates({
    date: new Date(bookedTo),
    includeAssignmentId: carrierAssignmentId,
  });
  const carrier = candidates.data?.find(
    (c) => c.assignmentID === carrierAssignmentId
  );

  if (!carrier) {
    return <span>Uppdrag {carrierAssignmentId}</span>;
  }

  return (
    <LabelTextButton
      onClick={() => openCarrierCase(carrier)}
      title="Öppna bärarens ärende"
    >
      {candidateLabel(carrier)}
    </LabelTextButton>
  );
};

export default RideAlongCarrierPicker;
